import {
  Agent,
  Customization,
  CustomTheme,
  getPricingsByAgentId,
  PaymentMethodType,
  postCreateCheckoutSession,
  Product,
  Shape,
  UiMode,
} from '@nemuru-eng/js-api-client';
import { v4 as uuidv4 } from 'uuid';

import { AbstractPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { CheckoutSessionCustomerData } from '@/components/modules/Application/types/CheckoutSessionCustomerData';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useRequest } from '@/hooks/requests/useRequest';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const useCreateCheckoutSession = () => {
  const { request, loading } = useRequest();

  const { palette, customBorderRadius, background } = useCustomTheme();

  const {
    state: {
      cart,
      downPayment,
      paymentMethodEntities,
      selectedAgent,
      orderReference1,
      orderReference2,
      customerData,
      checkoutSessionId,
    },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const createCheckoutSession = async (customer?: CheckoutSessionCustomerData) => {
    const orderReference = (orderReference1 || customer?.documentNumber || uuidv4().split('-')[4]).trim();

    if (!cart || !selectedAgent || !orderReference || !checkoutSessionId) {
      return;
    }

    const customization = getCustomizationByAgent({ agent: selectedAgent });

    const firstName = customer?.firstName || customerData?.firstName;
    const lastName = customer?.lastName || customerData?.lastName;
    const documentNumber = customer?.documentNumber || customerData?.documentNumber;
    const email = customer?.email || customerData?.email;
    const phoneNumber = customer?.phoneNumber || customerData?.phoneNumber;

    let newCustomerData: CheckoutSessionCustomerData | null = null;

    if (firstName && lastName) {
      newCustomerData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        documentNumber: documentNumber?.trim(),
        email: email?.trim(),
        phoneNumber: phoneNumber?.trim(),
      };
    }

    const response = await request(postCreateCheckoutSession, {
      id: checkoutSessionId,
      agentId: selectedAgent.id,
      merchant: {
        orderReference1: orderReference,
        ...(orderReference2 && { orderReference2 }),
      },
      cart: cart.toDto(),
      ...(downPayment.amount.amount > 0 && {
        downPayment: {
          amount: downPayment.amount.amount,
          currency: downPayment.amount.currency,
          downPaymentCollection: downPayment.downPaymentCollection,
        },
      }),
      ...(newCustomerData ? { customer: newCustomerData } : {}),
      ui: {
        mode: UiMode.HPP,
        ...(customization && { customization }),
      },
      ...(await getPaymentMethodOptions(paymentMethodEntities)),
    });

    if (response && 'hppId' in response) {
      updateApplicationContext({
        hppId: response.hppId,
        qrCodeUrl: response.qrCodeUrl,
        redirectUrl: response.redirectUrl,
      });
    }

    return response;
  };

  const getPaymentMethodOptions = async (paymentMethodEntities?: AbstractPaymentMethod[]) => {
    // TODO: Improve this (https://bitbucket.org/nemuru/nemuru-admin/pull-requests/1298 must be merged first)
    if (selectedAgent && selectedAgent.allowedProducts.includes(Product.CARD)) {
      return {
        paymentMethods: [
          {
            type: PaymentMethodType.CREDIT_CARD,
            options: {
              // TODO: make these dynamic or remove if not needed
              customerType: 'iberia-cards-stand',
              marketingCampaign: 'iberia-cards-stand',
              applicationOrigin: 0,
              channel: 'iberia-cards-stand',
            },
          },
        ],
      };
    }

    if (!paymentMethodEntities || paymentMethodEntities.length === 0) {
      return {};
    }

    if (!selectedAgent) {
      return {};
    }

    const agentPricings = await getPricingsByAgentId({ agentId: selectedAgent.id });

    return {
      paymentMethods: paymentMethodEntities
        .filter((paymentMethod) => paymentMethod.isSelected)
        .map((paymentMethod) => paymentMethod.toDto(agentPricings, cart)),
    };
  };

  const getCustomizationByAgent = ({ agent }: { agent: Agent }): Customization | undefined => {
    if (agent.customTheme !== CustomTheme.DEMO) {
      return;
    }
    return {
      shape:
        customBorderRadius.button === 0
          ? Shape.RECTANGULAR
          : customBorderRadius.button > 16
          ? Shape.PILL
          : Shape.ROUNDED,
      colorText: palette.text.primary,
      colorBrandForeground: palette.primary.contrastText,
      colorBrand: palette.primary.main,
      colorBorder: palette.grey[200],
      colorBackground: background ?? '#FFF',
    } as Customization;
  };

  return {
    createCheckoutSession,
    loading,
  };
};
